import { useEffect, useState } from "react";
import {
  Clock,
  NotePencil,
  CurrencyCircleDollar,
  Check,
  X,
  CheckCircle,
  Sparkle,
  BellRinging,
  ArrowLeft,
} from "@phosphor-icons/react";
import MarkerStroke from "./MarkerStroke";
import useReveal from "./useReveal";
import "./home.css";
import "./HomePendingActions.css";

/*
 * "פעולות ממתינות" — the product's signature daily loop, sold on the homepage.
 *
 * Left: copy + three numbered benefits (one per question the system asks).
 * Right: a static, auto-cycling mock of a pending-meeting card that mirrors
 * the real dashboard card (same three stages, same tints: amber / violet /
 * teal, done = mint). Purely presentational — no store, no Firebase.
 */

const STAGES = [
  {
    key: "approval",
    num: "1",
    icon: Clock,
    title: "הפגישה התקיימה?",
    text: "אישור בלחיצה אחת. ביטול? המערכת מציעה מיד לעדכן את המטופל ולשחרר את השעה ביומן.",
    question: "הפגישה עם נועה התקיימה?",
  },
  {
    key: "summary",
    num: "2",
    icon: NotePencil,
    title: "סיכום הטיפול נכתב?",
    text: "לחיצה פותחת את דף תיעוד הטיפול כשהפגישה כבר מקושרת. מקליטים או כותבים כמה שורות, וה-AI מנסח סיכום מקצועי.",
    question: "לכתוב עכשיו סיכום טיפול?",
  },
  {
    key: "payment",
    num: "3",
    icon: CurrencyCircleDollar,
    title: "התשלום התקבל?",
    text: "הסכום כבר ממולא לפי התעריף של המטופל. שולם? החשבונית יוצאת. עוד לא? נפתחת תזכורת גבייה אוטומטית.",
    question: "התקבל תשלום של 350 ₪?",
  },
];

const CYCLE_MS = 2600;

function HomePendingActions() {
  const ref = useReveal();
  // Mock state: how many stages are already done (0..3), loops forever.
  // Starts at 1 so the static (prerendered / reduced-motion) frame already
  // shows done + active + waiting together.
  const [doneCount, setDoneCount] = useState(1);

  useEffect(() => {
    if (typeof window === "undefined" || navigator.webdriver) return undefined;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return undefined;
    const id = setInterval(() => setDoneCount((n) => (n + 1) % 4), CYCLE_MS);
    return () => clearInterval(id);
  }, []);

  const allDone = doneCount === 3;

  return (
    <section id="pending" className="home-section home-section--white hpend" aria-labelledby="hpend-title">
      <div className="home-container">
        <div className="hpend-grid home-reveal" ref={ref}>
          <div className="hpend-copy">
            <h2 id="hpend-title" className="home-h2 home-h2--display hpend-title">
              שיטת{" "}
              <span className="home-mark">
                שלוש השאלות
                <MarkerStroke />
              </span>
            </h2>
            <p className="home-sub">
              שיטה ייחודית שאופיינה יחד עם מטפלים ובעלי קליניקות, כדי ששום דבר לא ייפול בין
              הכיסאות. בסיום כל פגישה המערכת מבקשת מכם לעדכן שלושה סטטוסים קצרים: האם הפגישה
              התקיימה, האם נכתב סיכום טיפול והאם התקבל תשלום. שלוש לחיצות, והיומן, הכספים ותיק
              המטופל מתעדכנים לבד. בלי רשימות בצד, בלי "אני אזכור".
            </p>

            <ol className="hpend-steps">
              {STAGES.map((s) => {
                const Icon = s.icon;
                return (
                  <li key={s.key} className="hpend-step" data-stage={s.key}>
                    <span className="hpend-step-icon" aria-hidden="true">
                      <Icon size={20} weight="duotone" />
                    </span>
                    <div>
                      <h3>
                        <span className="hpend-step-num">{s.num}</span>
                        {s.title}
                      </h3>
                      <p>{s.text}</p>
                    </div>
                  </li>
                );
              })}
            </ol>

            <ul className="hpend-perks">
              <li>
                <Sparkle size={16} weight="fill" />
                סיכום טיפול עם AI מתוך הקלטה או כמה מילים
              </li>
              <li>
                <BellRinging size={16} weight="fill" />
                תזכורות גבייה ותיעוד נוצרות לבד כשעונים "עוד לא"
              </li>
              <li>
                <CheckCircle size={16} weight="fill" />
                דקה ביום, והקליניקה סגורה: יומן, כספים ותיקים מעודכנים
              </li>
            </ul>

            <a href="#contact" className="home-btn home-btn--primary hpend-cta">
              רוצים לראות את זה על הקליניקה שלכם?
              <ArrowLeft size={18} weight="bold" />
            </a>
          </div>

          {/* ---- Mock pending-meeting card (presentational) ---- */}
          <div className="hpend-mock" aria-hidden="true">
            <div className="hpend-mock-label">
              <span className="hpend-mock-dot" />
              פעולות ממתינות
              <span className="hpend-mock-count">{allDone ? 0 : 3 - doneCount}</span>
            </div>

            <div className={`hpend-card${allDone ? " is-done" : ""}`}>
              <div className="hpend-card-head">
                <div className="hpend-avatar">נ</div>
                <div className="hpend-card-who">
                  <strong>נועה לוי</strong>
                  <span>אתמול · 16:00 · טיפול פרטני</span>
                </div>
                <span className="hpend-card-chip">
                  {allDone ? "הושלם" : `${doneCount}/3`}
                </span>
              </div>

              <div className="hpend-stages">
                {STAGES.map((s, idx) => {
                  const Icon = s.icon;
                  const isDone = idx < doneCount;
                  const isActive = idx === doneCount;
                  return (
                    <div
                      key={s.key}
                      className={`hpend-stage${isDone ? " is-done" : ""}${isActive ? " is-active" : ""}`}
                      data-stage={s.key}
                    >
                      <div className="hpend-stage-head">
                        <span className="hpend-stage-title">
                          {isDone ? <Check size={14} weight="bold" /> : <Icon size={14} weight="fill" />}
                          {s.title}
                        </span>
                        <span className="hpend-stage-badge">{isDone ? "עודכן" : isActive ? "נדרש עדכון" : "ממתין"}</span>
                      </div>

                      {isActive && (
                        <div className="hpend-stage-body">
                          <span>{s.question}</span>
                          <span className="hpend-keys">
                            <span className="hpend-key hpend-key--yes">
                              <Check size={13} weight="bold" />
                            </span>
                            <span className="hpend-key hpend-key--no">
                              <X size={13} weight="bold" />
                            </span>
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {allDone && (
                <div className="hpend-card-done">
                  <CheckCircle size={18} weight="fill" />
                  הפגישה סגורה. היומן, הכספים ותיק המטופל מעודכנים.
                </div>
              )}
            </div>

            <div className="hpend-mock-ghost" />
            <div className="hpend-mock-ghost hpend-mock-ghost--2" />
          </div>
        </div>
      </div>
    </section>
  );
}

export default HomePendingActions;
